import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, LockKeyhole, RefreshCw, XCircle } from "lucide-react";

import PilotageField from "@/components/pilotage/PilotageField";
import PilotageTable from "@/components/pilotage/PilotageTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import { resolveMissionTimesheetFallbackDays } from "@/lib/businessRules/timesheets";
import { formatPct, resolveIntermediairePct } from "@/lib/businessRules";
import { emitTimesheetUpdated } from "@/lib/dataEvents";
import {
  buildDefaultExpenseForm,
  buildExpensePayload,
  normalizeExpenseForForm,
  resolveExpensePeriodType,
} from "@/lib/pilotageExpenses";
import {
  EXPENSE_CATEGORIES,
  approveExpense,
  createExpense,
  createTimesheet,
  deleteExpense,
  getMissionFinancials,
  getMission,
  listClients,
  listCollaborators,
  listExpenses,
  listTimesheetMonths,
  lockTimesheet,
  rejectExpense,
  updateExpense,
  updateTimesheet,
} from "@/lib/pilotageApi";
import {
  CONSULTANT_RATE_LABEL,
  INTERMEDIARY_MARGIN_LABEL,
  TACE_LABEL,
  formatMonthLabel,
  formatDateLabel,
  formatEUR0,
  formatPct0,
  resolveCollaboratorContractType,
  resolveMissionTypeWithCollaborator,
  resolveEmployerCostCoefficient,
  toMonthKey,
} from "@/lib/pilotageUi";
import { getAvailableMonthsForTimesheet } from "@/lib/timesheetRules";

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatExpenseModeLabel = (expense) => {
  const periodType = resolveExpensePeriodType(expense);
  if (periodType === "ONE_TIME") {
    return "Ponctuel";
  }
  if (periodType === "RANGE") {
    return "Plage mensuelle";
  }
  return "Permanent mensuel";
};

const formatExpensePeriodLabel = (expense) => {
  const periodType = resolveExpensePeriodType(expense);
  const startDate = expense?.start_date || expense?.date;

  if (periodType === "ONE_TIME") {
    return `Le ${formatDateLabel(startDate)}`;
  }

  if (periodType === "RANGE") {
    return `Du ${formatDateLabel(startDate)} au ${formatDateLabel(expense?.end_date)}`;
  }

  return `A partir du ${formatDateLabel(startDate)}`;
};

const formatExpenseAmountLabel = (expense) => {
  if (resolveExpensePeriodType(expense) === "ONE_TIME") {
    return formatEUR0(expense?.amount);
  }

  return `${formatEUR0(expense?.amount)} / mois`;
};

const MissionDetailPage = () => {
  const { missionId } = useParams();
  const { role } = useAuth();
  const isRh = role === "RH";

  const [mission, setMission] = useState(null);
  const [missionFinancials, setMissionFinancials] = useState(null);
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [timesheetRows, setTimesheetRows] = useState([]);
  const [timesheetsLoading, setTimesheetsLoading] = useState(true);
  const [timesheetError, setTimesheetError] = useState("");
  const [timesheetBusyKey, setTimesheetBusyKey] = useState("");
  const [timesheetEditRows, setTimesheetEditRows] = useState({});

  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(true);
  const [expenseError, setExpenseError] = useState("");
  const [expenseBusyId, setExpenseBusyId] = useState("");
  const [expenseFilterMonth, setExpenseFilterMonth] = useState("");
  const [expenseEditingId, setExpenseEditingId] = useState("");
  const [expenseForm, setExpenseForm] = useState(buildDefaultExpenseForm);


  const collaborator = collaborators.find((item) => item.id === mission?.collaborator_id);
  const client = clients.find((item) => item.id === mission?.client_id);
  const hydrateTimesheetRows = useCallback(async () => {
    if (!missionId || !mission) {
      return;
    }

    setTimesheetsLoading(true);
    setTimesheetError("");

    try {
      const currentMonthKey = toMonthKey(new Date());
      const monthsResponse = await listTimesheetMonths(missionId);
      const months = Array.isArray(monthsResponse) ? monthsResponse : [];
      const monthsByKey = new Map(
        months
          .map((entry) => [String(entry?.month || ""), entry])
          .filter(([monthKey]) => Boolean(monthKey)),
      );
      const availableMonths = getAvailableMonthsForTimesheet({
        contractStartDate: mission?.start_date,
        contractEndDate: mission?.end_date,
        now: new Date(),
      });
      const fallbackForecastDays = String(resolveMissionTimesheetFallbackDays(mission));
      setTimesheetRows(
        availableMonths.map((availableMonth) => {
          const entry = monthsByKey.get(availableMonth.key) || {
            month: availableMonth.key,
            state: "OPEN",
            locked: false,
            timesheet_id: null,
            declared_days: null,
            forecast_days: resolveMissionTimesheetFallbackDays(mission),
          };

          return {
            key: entry.month,
            label: formatMonthLabel(entry.month),
            id: entry.timesheet_id || null,
            locked: entry.locked || entry.state === "CLOSED",
            isFuture: entry.month > currentMonthKey,
            isPast: entry.month < currentMonthKey,
            isCurrent: entry.month === currentMonthKey,
            days_declared:
              entry.declared_days === null || entry.declared_days === undefined
                ? ""
                : String(entry.declared_days),
            days_forecast:
              entry.forecast_days === null || entry.forecast_days === undefined
                ? fallbackForecastDays
                : String(entry.forecast_days),
            client_free_days:
              entry.client_free_days === null || entry.client_free_days === undefined
                ? ""
                : String(entry.client_free_days),
            client_free_days_initial: Number(entry.client_free_days || 0),
            billed_days:
              entry.billed_days === null || entry.billed_days === undefined
                ? null
                : Number(entry.billed_days),
          };
        }),
      );
    } catch (requestError) {
      setTimesheetError(
        getApiErrorMessage(requestError, "Impossible de charger les jours de mission."),
      );
    } finally {
      setTimesheetsLoading(false);
    }
  }, [mission, missionId]);

  const loadExpenses = useCallback(async (month = "") => {
    if (!missionId) {
      return;
    }

    setExpensesLoading(true);
    setExpenseError("");

    try {
      const data = await listExpenses({
        mission_id: missionId,
        month: month || undefined,
      });
      setExpenses(data);
    } catch (requestError) {
      setExpenseError(
        getApiErrorMessage(requestError, "Impossible de charger les frais."),
      );
    } finally {
      setExpensesLoading(false);
    }
  }, [missionId]);

  const loadMissionFinancials = useCallback(async () => {
    if (!missionId) {
      return;
    }

    try {
      const missionFinancialsData = await getMissionFinancials(missionId);
      setMissionFinancials(missionFinancialsData);
    } catch {
      setMissionFinancials(null);
    }
  }, [missionId]);

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      if (!missionId) {
        setPageError("Mission introuvable.");
        setPageLoading(false);
        return;
      }

      setPageLoading(true);
      setPageError("");

      try {
        const [missionData, clientsData, collaboratorsData, missionFinancialsData] = await Promise.all([
          getMission(missionId),
          listClients(),
          listCollaborators(),
          getMissionFinancials(missionId).catch(() => null),
        ]);

        if (cancelled) {
          return;
        }

        setMission(missionData);
        setMissionFinancials(missionFinancialsData);
        setClients(clientsData);
        setCollaborators(collaboratorsData);
      } catch (requestError) {
        if (cancelled) {
          return;
        }

        setPageError(
          getApiErrorMessage(requestError, "Impossible de charger la mission."),
        );
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    void loadPage();

    return () => {
      cancelled = true;
    };
  }, [missionId]);

  useEffect(() => {
    if (!missionId || !mission) {
      return;
    }

    void hydrateTimesheetRows();
    void loadExpenses("");
  }, [mission, missionId, hydrateTimesheetRows, loadExpenses]);

  useEffect(() => {
    if (!missionId) {
      return;
    }

    void loadExpenses(expenseFilterMonth);
  }, [expenseFilterMonth, missionId, loadExpenses]);

  const updateTimesheetDraft = (monthKey, field, value) => {
    setTimesheetRows((current) =>
      current.map((row) =>
        row.key === monthKey
          ? {
            ...row,
            [field]: value,
          }
          : row,
      ),
    );
  };

  const enableLockedTimesheetEdit = (monthKey) => {
    setTimesheetEditRows((current) => ({
      ...current,
      [monthKey]: true,
    }));
  };

  const clearLockedTimesheetEdit = (monthKey) => {
    setTimesheetEditRows((current) => {
      if (!current[monthKey]) {
        return current;
      }

      const nextState = { ...current };
      delete nextState[monthKey];
      return nextState;
    });
  };

  const persistTimesheetRow = async (row) => {
    const fallbackForecastDays = resolveMissionTimesheetFallbackDays(mission);
    const payload = row.isFuture
      ? {
        days_forecast: Number(row.days_forecast || fallbackForecastDays),
      }
      : {
        days_declared:
          row.days_declared === "" ? null : Number(row.days_declared),
        days_forecast: Number(row.days_forecast || fallbackForecastDays),
        client_free_days:
          row.client_free_days === "" || row.client_free_days === undefined
            ? 0
            : Number(row.client_free_days),
      };

    if (row.id) {
      return updateTimesheet(row.id, payload);
    }

    return createTimesheet({
      mission_id: missionId,
      month: row.key,
      ...payload,
    });
  };

  const handleSaveTimesheet = async (row) => {
    setTimesheetBusyKey(`save:${row.key}`);
    setTimesheetError("");

    try {
      await persistTimesheetRow(row);
      clearLockedTimesheetEdit(row.key);
      emitTimesheetUpdated({
        collaboratorId: String(mission?.collaborator_id || ""),
        missionId: String(missionId || ""),
        monthKey: row.key,
      });
      await Promise.all([
        hydrateTimesheetRows(),
        getMission(missionId).then((data) => setMission(data)).catch(() => {}),
        loadMissionFinancials(),
      ]);
    } catch (requestError) {
      setTimesheetError(
        getApiErrorMessage(requestError, "Impossible d'enregistrer le timesheet."),
      );
    } finally {
      setTimesheetBusyKey("");
    }
  };

  const handleLockTimesheet = async (row) => {
    setTimesheetBusyKey(`lock:${row.key}`);
    setTimesheetError("");

    try {
      const savedRow = await persistTimesheetRow(row);
      await lockTimesheet(savedRow.id);
      emitTimesheetUpdated({
        collaboratorId: String(mission?.collaborator_id || ""),
        missionId: String(missionId || ""),
        monthKey: row.key,
      });
      await hydrateTimesheetRows();
    } catch (requestError) {
      setTimesheetError(
        getApiErrorMessage(requestError, "Impossible de clôturer le timesheet."),
      );
    } finally {
      setTimesheetBusyKey("");
    }
  };

  const resetExpenseForm = () => {
    setExpenseEditingId("");
    setExpenseForm(buildDefaultExpenseForm());
  };

  const handleSaveExpense = async (event) => {
    event.preventDefault();
    const isEditingExpense = Boolean(expenseEditingId);
    setExpenseBusyId(isEditingExpense ? "update" : "create");
    setExpenseError("");

    try {
      const payload = buildExpensePayload(expenseForm, {
        missionId,
        includeMissionId: !isEditingExpense,
      });

      if (isEditingExpense) {
        await updateExpense(expenseEditingId, payload);
      } else {
        await createExpense(payload);
      }

      resetExpenseForm();
      await Promise.all([
        loadExpenses(expenseFilterMonth),
        loadMissionFinancials(),
      ]);
    } catch (requestError) {
      setExpenseError(
        getApiErrorMessage(
          requestError,
          isEditingExpense
            ? "Impossible de modifier le frais."
            : "Impossible d'ajouter le frais.",
        ),
      );
    } finally {
      setExpenseBusyId("");
    }
  };

  const handleEditExpense = (expense) => {
    setExpenseEditingId(expense.id);
    setExpenseForm(normalizeExpenseForForm(expense));
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Supprimer ce frais mission ?")) {
      return;
    }

    setExpenseBusyId(`delete:${expenseId}`);
    setExpenseError("");

    try {
      await deleteExpense(expenseId);
      if (expenseEditingId === expenseId) {
        resetExpenseForm();
      }
      await Promise.all([
        loadExpenses(expenseFilterMonth),
        loadMissionFinancials(),
      ]);
    } catch (requestError) {
      setExpenseError(
        getApiErrorMessage(requestError, "Impossible de supprimer le frais."),
      );
    } finally {
      setExpenseBusyId("");
    }
  };

  const handleExpenseDecision = async (expenseId, action) => {
    setExpenseBusyId(`${action}:${expenseId}`);
    setExpenseError("");

    try {
      if (action === "approve") {
        await approveExpense(expenseId);
      } else {
        await rejectExpense(expenseId);
      }

      await Promise.all([
        loadExpenses(expenseFilterMonth),
        loadMissionFinancials(),
      ]);
    } catch (requestError) {
      setExpenseError(
        getApiErrorMessage(requestError, "Impossible de mettre à jour le frais."),
      );
    } finally {
      setExpenseBusyId("");
    }
  };

  if (pageLoading) {
    return (
      <div className="rounded-3xl border border-white/10 glass p-10 text-center text-sm text-slate-300">
        Chargement de la mission...
      </div>
    );
  }

  if (pageError || !mission) {
    return (
      <div className="space-y-4">
        <Button asChild variant="outline" className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white">
          <Link to="/admin/pilotage/missions">
            <ArrowLeft className="h-4 w-4" />
            Retour aux missions
          </Link>
        </Button>
        <div className="rounded-3xl border border-red-500/20 bg-red-500/10 px-5 py-4 text-sm text-red-200">
          {pageError || "Mission introuvable."}
        </div>
      </div>
    );
  }

  const missionType = resolveMissionTypeWithCollaborator(mission, collaborator);
  const missionTypeCostLabel =
    missionType === "FREELANCE" ? CONSULTANT_RATE_LABEL : "Salaire annuel";
  const missionTypeCostValue =
    missionType === "FREELANCE"
      ? formatEUR0(mission.tjm_consultant)
      : formatEUR0(mission.salaire_annuel);
  const collaboratorContractType = resolveCollaboratorContractType(collaborator);
  const showsTace = missionType !== "FREELANCE";
  const employerCostCoeff = resolveEmployerCostCoefficient(mission);
  const cjmValue =
    missionType === "FREELANCE"
      ? Number(mission.tjm_consultant || 0)
      : (Number(mission.salaire_annuel || 0) * employerCostCoeff) / 210;
  const financialMetrics = {
    caMensuelNet: toOptionalNumber(
      missionFinancials?.ca_mensuel_estime
      ?? missionFinancials?.ca_mensuel_net_estime
      ?? missionFinancials?.ca_mensuel_net,
    ),
    caAnnuelNet: toOptionalNumber(
      missionFinancials?.ca_annuel_estime
      ?? missionFinancials?.ca_annuel_net_estime
      ?? missionFinancials?.ca_annuel_net,
    ),
    margePct: toOptionalNumber(
      missionFinancials?.margin_with_intermediary_pct
      ?? missionFinancials?.marge_pct_net
      ?? missionFinancials?.marge_pct
      ?? missionFinancials?.margin_pct,
    ),
    beneficeMensuel: toOptionalNumber(missionFinancials?.benefice_mensuel_estime),
    beneficeAnnuel: toOptionalNumber(missionFinancials?.benefice_annuel_estime),
    missionExpensesMonthly: toOptionalNumber(missionFinancials?.frais_mission_mensuels_estimes),
    missionExpensesAnnual: toOptionalNumber(missionFinancials?.frais_mission_annuels_estimes),
  };
  let contactText = "—";
  let contactEmail = "";
  if (mission?.client_contact_id) {
    const missionContact = client?.contacts?.find((c) => c.id === mission.client_contact_id);
    if (missionContact) {
      contactText = `${missionContact.full_name}${missionContact.role ? ` — ${missionContact.role}` : ""}${missionContact.department ? ` ${missionContact.department}` : ""}`;
      contactEmail = missionContact.email || "";
    } else {
      contactText = mission.client_contact_name || "—";
    }
  } else {
    const primaryContact = client?.contacts?.find((c) => c.is_primary) || client?.contacts?.[0];
    if (primaryContact) {
      contactText = `${primaryContact.full_name}${primaryContact.role ? ` — ${primaryContact.role}` : ""}${primaryContact.department ? ` ${primaryContact.department}` : ""}`;
      contactEmail = primaryContact.email || "";
    } else if (mission?.client_contact_name) {
      contactText = mission.client_contact_name;
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="outline" className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white">
          <Link to="/admin/pilotage/missions">
            <ArrowLeft className="h-4 w-4" />
            Retour aux missions
          </Link>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void hydrateTimesheetRows();
            void loadExpenses(expenseFilterMonth);
            void loadMissionFinancials();
          }}
          className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
        >
          <RefreshCw className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="grid gap-4 lg:grid-cols-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Client
            </p>
            <p className="mt-3 text-lg font-semibold text-white">
              {client?.name || mission.client_id}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              {client?.sector || "Secteur non renseigné"}
            </p>
            <div className="mt-3 pt-3 border-t border-white/5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                Contact client
              </p>
              <p className="mt-1 text-sm font-medium text-slate-200">
                {contactText}
              </p>
              {contactEmail && (
                <p className="text-xs text-slate-400 mt-0.5 break-all">
                  {contactEmail}
                </p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Collaborateur
            </p>
            <p className="mt-3 text-lg font-semibold text-white">
              {collaborator?.full_name || mission.collaborator_id}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              {collaboratorContractType || "Contrat non renseigné"}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Rôle mission
            </p>
            <p className="mt-3 text-lg font-semibold text-white">
              {mission.mission_role || "—"}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              Qualification métier de la mission
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Type
            </p>
            <p className="mt-3 text-lg font-semibold text-white">
              {missionType}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              Format de mission retenu pour le pilotage financier
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Période
            </p>
            <p className="mt-3 text-lg font-semibold text-white">
              {formatDateLabel(mission.start_date)}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              {mission.end_date
                ? `Jusqu’au ${formatDateLabel(mission.end_date)}`
                : `Statut ${mission.status}`}
            </p>
          </div>
        </div>

        {!isRh ? (
          <div className={`mt-4 grid gap-4 ${showsTace ? "lg:grid-cols-5" : "lg:grid-cols-3"}`}>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                TJM client
              </p>
              <p className="mt-2 text-base font-semibold text-white">
                {formatEUR0(mission.tarif_client)}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {missionTypeCostLabel}
              </p>
              <p className="mt-2 text-base font-semibold text-white">
                {missionTypeCostValue}
              </p>
            </div>
            {showsTace ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Coeff. employeur
                </p>
                <p className="mt-2 text-base font-semibold text-white" data-testid="mission-employer-cost-coeff-display">
                  {employerCostCoeff.toFixed(2).replace(".", ",")}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Coût chargé: {formatEUR0(Number(mission.salaire_annuel || 0) * employerCostCoeff)}
                </p>
              </div>
            ) : null}
            {showsTace ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {TACE_LABEL}
                </p>
                <p className="mt-2 text-base font-semibold text-white">
                  {formatPct0(mission.tace)}
                </p>
              </div>
            ) : null}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {INTERMEDIARY_MARGIN_LABEL}
              </p>
              <p className="mt-2 text-base font-semibold text-white">
                {mission?.intermediaire_margin_mode === "AMOUNT"
                  ? `${formatEUR0(mission.intermediaire_margin_amount)}/jour (équiv. ${formatPct(resolveIntermediairePct(mission))})`
                  : `${formatPct(resolveIntermediairePct(mission))} (équiv. ${formatEUR0(mission.intermediaire_margin_amount ?? (mission.tarif_client * resolveIntermediairePct(mission)))}/jour)`
                }
              </p>
            </div>
            {((mission.client_free_days_granted || 0) > 0 || (mission.client_free_days_consumed || 0) > 0) ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Gratuité client
                  </p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-lenvia-blue/10 text-lenvia-blue border border-lenvia-blue/20 font-medium">
                    {mission.client_free_days_remaining ?? 0} j restants
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-center border-t border-white/5 pt-2">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Accordés</p>
                    <p className="text-sm font-bold text-white mt-0.5">{mission.client_free_days_granted ?? 0} j</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Consommés</p>
                    <p className="text-sm font-bold text-amber-400 mt-0.5">{mission.client_free_days_consumed ?? 0} j</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Restants</p>
                    <p className="text-sm font-bold text-emerald-400 mt-0.5">{mission.client_free_days_remaining ?? 0} j</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="rounded-3xl border border-white/10 glass p-6">
        <h2 className="text-lg font-semibold text-white">Pilotage financier</h2>
        <p className="mt-1 text-sm text-slate-400">
          Valeurs calculées côté backend via les règles unifiées Pilotage. Inclut coûts consultant et frais mission approuvés applicables a la période.
        </p>

        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
            data-testid="mission-finance-ca-monthly"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              CA mensuel net (estimé)
            </p>
            <p className="mt-2 text-base font-semibold text-white">
              {formatEUR0(financialMetrics.caMensuelNet)}
            </p>
          </div>

          <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
            data-testid="mission-finance-ca-annual"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              CA annuel net (estimé)
            </p>
            <p className="mt-2 text-base font-semibold text-white">
              {formatEUR0(financialMetrics.caAnnuelNet)}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Frais mission (mois)
            </p>
            <p className="mt-2 text-base font-semibold text-white">
              {formatEUR0(financialMetrics.missionExpensesMonthly)}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Bénéfice annuel (estimé)
            </p>
            <p className="mt-2 text-base font-semibold text-white">
              {formatEUR0(financialMetrics.beneficeAnnuel)}
            </p>
          </div>

          <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
            data-testid="mission-finance-margin"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Marge nette mission
            </p>
            <p className="mt-2 text-base font-semibold text-white">
              {formatPct0(financialMetrics.margePct)}
            </p>
          </div>
        </div>
      </section>

      <Tabs defaultValue="timesheets" className="space-y-5">
        <TabsList className="bg-white/5">
          <TabsTrigger value="timesheets">Jours</TabsTrigger>
          <TabsTrigger value="expenses">Frais</TabsTrigger>
        </TabsList>

        <TabsContent value="timesheets" className="space-y-4" data-testid="mission-timesheets-tab">
          {timesheetError ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              <span>{timesheetError}</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void hydrateTimesheetRows();
                }}
                className="border-red-400/30 text-red-100 hover:bg-red-500/10 hover:text-white"
              >
                Réessayer
              </Button>
            </div>
          ) : null}

          {((mission?.client_free_days_granted || 0) > 0 || (mission?.client_free_days_consumed || 0) > 0) ? (
            <div className="flex items-center justify-between rounded-2xl border border-lenvia-blue/20 bg-lenvia-blue/5 px-4 py-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white">Gratuité négociée :</span>
                <span className="text-slate-300">
                  {mission.client_free_days_granted || 0} jours accordés au client sur la mission.
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span>Consommés : <strong className="text-amber-400">{mission.client_free_days_consumed || 0} j</strong></span>
                <span>Restants : <strong className="text-emerald-400">{mission.client_free_days_remaining ?? 0} j</strong></span>
              </div>
            </div>
          ) : null}

          <PilotageTable
            columns={[
              {
                key: "label",
                label: "Mois",
                render: (row) => (
                  <div>
                    <p className="font-medium text-white">{row.label}</p>
                    <p className="mt-1 text-xs text-slate-500">{row.key}</p>
                  </div>
                ),
              },
              {
                key: "days_declared",
                label: ((mission?.client_free_days_granted || 0) > 0 || (mission?.client_free_days_consumed || 0) > 0) ? "Jours travaillés" : "Jours déclarés",
                render: (row) => (
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={row.days_declared}
                    onChange={(event) =>
                      updateTimesheetDraft(row.key, "days_declared", event.target.value)
                    }
                    disabled={row.isFuture || (row.locked && !timesheetEditRows[row.key])}
                    className="border-white/10 bg-slate-950 text-white disabled:opacity-60"
                  />
                ),
              },
              ...(((mission?.client_free_days_granted || 0) > 0 || (mission?.client_free_days_consumed || 0) > 0)
                ? [
                    {
                      key: "client_free_days",
                      label: "Jours gratuits",
                      render: (row) => {
                        if (row.isFuture) {
                          return <span className="text-slate-500">—</span>;
                        }
                        const isLockedRow = row.locked && !timesheetEditRows[row.key];
                        const lineFreeDays = Number(row.client_free_days || 0);
                        const remainingBalance = mission?.client_free_days_remaining ?? 0;
                        const initialForLine = row.client_free_days_initial ?? 0;
                        const availableForLine = Math.max(0, remainingBalance + initialForLine);

                        if (remainingBalance === 0 && lineFreeDays === 0) {
                          return (
                            <span className="text-xs text-slate-500 italic">
                              0 j dispo
                            </span>
                          );
                        }

                        return (
                          <div className="space-y-1">
                            <Input
                              type="number"
                              min="0"
                              max={Math.min(Number(row.days_declared || 0), availableForLine)}
                              step="0.5"
                              value={row.client_free_days}
                              placeholder="0"
                              onChange={(event) => {
                                const val = event.target.value;
                                updateTimesheetDraft(row.key, "client_free_days", val);
                              }}
                              disabled={isLockedRow}
                              className="border-white/10 bg-slate-950 text-white disabled:opacity-60"
                            />
                            {availableForLine > 0 ? (
                              <p className="text-[10px] text-slate-500">
                                Max {availableForLine} j
                              </p>
                            ) : null}
                          </div>
                        );
                      },
                    },
                    {
                      key: "days_billed",
                      label: "Jours facturables",
                      render: (row) => {
                        if (row.isFuture) {
                          return <span className="text-slate-500">—</span>;
                        }
                        const declared = Number(row.days_declared || 0);
                        const free = Number(row.client_free_days || 0);
                        const billed = Math.max(0, declared - free);
                        const tjmClient = Number(mission?.tarif_client || 0);
                        const caMonth = billed * tjmClient;
                        const costMonth = declared * cjmValue;
                        const marginMonth = caMonth - costMonth;

                        return (
                          <div className="space-y-1.5">
                            <div className="text-sm font-semibold text-white">
                              <span>{billed} j</span>
                              {free > 0 ? (
                                <span className="block text-[10px] text-amber-400 font-normal">
                                  (-{free} j gratuit{free > 1 ? "s" : ""})
                                </span>
                              ) : null}
                            </div>
                            {free > 0 && tjmClient > 0 ? (
                              <div
                                data-testid={`financial-breakdown-${row.key}`}
                                className="rounded-lg border border-white/10 bg-white/[0.03] p-2 text-[11px] leading-tight space-y-1 min-w-[170px]"
                              >
                                <div className="flex justify-between text-slate-400">
                                  <span>Travaillés:</span>
                                  <span className="text-slate-200 font-medium">{declared} j</span>
                                </div>
                                <div className="flex justify-between text-amber-400/90">
                                  <span>Gratuits:</span>
                                  <span className="font-medium">{free} j</span>
                                </div>
                                <div className="flex justify-between text-slate-400">
                                  <span>Facturables:</span>
                                  <span className="text-white font-semibold">{billed} j</span>
                                </div>
                                <div className="border-t border-white/5 pt-1 space-y-0.5">
                                  <div className="flex justify-between text-slate-400">
                                    <span>CA:</span>
                                    <span className="text-slate-200 font-medium">{formatEUR0(caMonth)}</span>
                                  </div>
                                  <div className="flex justify-between text-slate-400">
                                    <span>Coût LENVIA:</span>
                                    <span className="text-slate-200 font-medium">{formatEUR0(costMonth)}</span>
                                  </div>
                                  <div className="flex justify-between font-semibold border-t border-white/5 pt-0.5">
                                    <span className="text-slate-300">Marge:</span>
                                    <span className={marginMonth >= 0 ? "text-emerald-400" : "text-red-400"}>
                                      {formatEUR0(marginMonth)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      },
                    },
                  ]
                : []),
              {
                key: "days_forecast",
                label: "Prévision",
                render: (row) => (
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={row.days_forecast}
                    onChange={(event) =>
                      updateTimesheetDraft(row.key, "days_forecast", event.target.value)
                    }
                    disabled={!row.isFuture}
                    className="border-white/10 bg-slate-950 text-white disabled:opacity-60"
                  />
                ),
              },
              {
                key: "status",
                label: "État",
                render: (row) => (row.locked ? "Clôturé" : row.isFuture ? "Prévision" : "Ouvert"),
              },
            ]}
            rows={timesheetRows}
            loading={timesheetsLoading}
            emptyMessage="Aucun mois disponible."
            renderActions={(row) => {
              const isSaving = timesheetBusyKey === `save:${row.key}`;
              const isLocking = timesheetBusyKey === `lock:${row.key}`;
              const isEditingLockedRow = row.locked && Boolean(timesheetEditRows[row.key]);
              const canSave = row.isFuture || row.days_declared !== "" || row.id;
              const canPrimaryAction = row.locked ? Boolean(row.id) : canSave;

              return (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!canPrimaryAction || isSaving || isLocking}
                    onClick={() => {
                      if (row.locked && !isEditingLockedRow) {
                        enableLockedTimesheetEdit(row.key);
                        return;
                      }

                      void handleSaveTimesheet(row);
                    }}
                    className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                  >
                    {isSaving
                      ? "..."
                      : row.locked && !isEditingLockedRow
                        ? "Modifier"
                        : "Enregistrer"}
                  </Button>
                  {row.isPast ? (
                    <Button
                      type="button"
                      size="sm"
                      disabled={row.locked || isSaving || isLocking}
                      onClick={() => handleLockTimesheet(row)}
                      className="btn-primary"
                    >
                      <LockKeyhole className="h-3.5 w-3.5" />
                      {isLocking ? "..." : "Clôturer"}
                    </Button>
                  ) : null}
                </div>
              );
            }}
          />
        </TabsContent>

        <TabsContent value="expenses" className="space-y-4">
          {expenseError ? (
            <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {expenseError}
            </div>
          ) : null}

          <section className="rounded-3xl border border-white/10 glass p-5">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-white">
                  {expenseEditingId ? "Modifier un frais mission" : "Ajouter un frais mission"}
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                  Ponctuel = montant unique. Plage et permanent = montant mensuel applique sur chaque mois couvert.
                </p>
              </div>

              <div className="w-full max-w-52">
                <PilotageField label="Filtre mois" htmlFor="expense-filter-month">
                  <Input
                    id="expense-filter-month"
                    type="month"
                    value={expenseFilterMonth}
                    onChange={(event) => setExpenseFilterMonth(event.target.value)}
                  />
                </PilotageField>
              </div>
            </div>

            <form className="grid gap-4 lg:grid-cols-4" onSubmit={handleSaveExpense}>
              <PilotageField label="Type de frais" htmlFor="expense-period-type">
                <select
                  id="expense-period-type"
                  value={expenseForm.periodType}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      periodType: event.target.value,
                      end_date: event.target.value === "RANGE" ? current.end_date : "",
                    }))
                  }
                  className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
                >
                  <option value="ONE_TIME">Ponctuel</option>
                  <option value="RANGE">Plage mensuelle</option>
                  <option value="ONGOING">Permanent mensuel</option>
                </select>
              </PilotageField>

              <PilotageField label="Début" htmlFor="expense-start-date">
                <Input
                  id="expense-start-date"
                  type="date"
                  value={expenseForm.start_date}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      start_date: event.target.value,
                    }))
                  }
                  required
                />
              </PilotageField>

              <PilotageField
                label={expenseForm.periodType === "ONE_TIME" ? "Fin" : "Fin (optionnelle)"}
                htmlFor="expense-end-date"
              >
                <Input
                  id="expense-end-date"
                  type="date"
                  value={expenseForm.periodType === "RANGE" ? expenseForm.end_date : ""}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      end_date: event.target.value,
                    }))
                  }
                  disabled={expenseForm.periodType !== "RANGE"}
                  required={expenseForm.periodType === "RANGE"}
                />
              </PilotageField>

              <PilotageField label="Catégorie" htmlFor="expense-category">
                <select
                  id="expense-category"
                  value={expenseForm.category}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      category: event.target.value,
                    }))
                  }
                  className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
                >
                  {EXPENSE_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </PilotageField>

              <PilotageField label="Montant" htmlFor="expense-amount">
                <Input
                  id="expense-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      amount: event.target.value,
                    }))
                  }
                  placeholder="0"
                  required
                />
              </PilotageField>

              <div className="lg:col-span-3">
                <PilotageField label="Commentaire" htmlFor="expense-comment">
                  <Textarea
                    id="expense-comment"
                    value={expenseForm.comment}
                    onChange={(event) =>
                      setExpenseForm((current) => ({
                        ...current,
                        comment: event.target.value,
                      }))
                    }
                    placeholder="Hotel, licence projet, environnement, abonnement..."
                    className="border-white/10 bg-slate-950 text-white"
                  />
                </PilotageField>
              </div>

              <div className="flex flex-wrap items-end justify-end gap-2 lg:col-span-4">
                {expenseEditingId ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetExpenseForm}
                    className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                  >
                    Annuler
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  className="btn-primary min-w-44"
                  disabled={expenseBusyId === "create" || expenseBusyId === "update"}
                >
                  {expenseBusyId === "update"
                    ? "Enregistrement..."
                    : expenseBusyId === "create"
                      ? "Ajout..."
                      : expenseEditingId
                        ? "Enregistrer le frais"
                        : "Ajouter le frais"}
                </Button>
              </div>
            </form>
          </section>

          <PilotageTable
            columns={[
              {
                key: "mode",
                label: "Type",
                render: (expense) => formatExpenseModeLabel(expense),
              },
              {
                key: "period",
                label: "Période",
                render: (expense) => formatExpensePeriodLabel(expense),
              },
              {
                key: "category",
                label: "Catégorie",
                render: (expense) => expense.category,
              },
              {
                key: "amount",
                label: "Montant",
                render: (expense) => formatExpenseAmountLabel(expense),
              },
              {
                key: "comment",
                label: "Commentaire",
                render: (expense) => expense.comment || "—",
              },
              {
                key: "status",
                label: "Statut",
                render: (expense) => expense.status,
              },
            ]}
            rows={expenses}
            loading={expensesLoading}
            emptyMessage="Aucun frais sur cette mission."
            renderActions={(expense) => (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleEditExpense(expense)}
                  disabled={expenseBusyId === "create" || expenseBusyId === "update"}
                  className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                >
                  Modifier
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteExpense(expense.id)}
                  disabled={expenseBusyId === `delete:${expense.id}`}
                  className="border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                >
                  {expenseBusyId === `delete:${expense.id}` ? "..." : "Supprimer"}
                </Button>
                {expense.status === "SUBMITTED" ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleExpenseDecision(expense.id, "approve")}
                      disabled={expenseBusyId === `approve:${expense.id}`}
                      className="btn-primary"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Approuver
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleExpenseDecision(expense.id, "reject")}
                      disabled={expenseBusyId === `reject:${expense.id}`}
                      className="border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Rejeter
                    </Button>
                  </>
                ) : null}
              </div>
            )}
          />
        </TabsContent>


      </Tabs>
    </div>
  );
};

export default MissionDetailPage;
